import is from '@sindresorhus/is';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { mapToAttributesWithValue } from '../../../../../shared/source/data-contract/custom-attribute';
import {
    singleUserOptionsSchema,
    userPaginationSchema,
    userSchema,
} from '../../../../../shared/source/data-contract/user';
import { getUserName } from '../../common/user';

import type { CustomAttributeWithValue } from '../../../../../shared/source/data-contract/custom-attribute';
import type { PaginatedResponse } from '../../../../../shared/source/data-contract/pagination';
import type { SingleUserOptions, User } from '../../../../../shared/source/data-contract/user';
import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type { DefaultError, InfiniteData, QueryKey } from '@tanstack/query-core';
import type { UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query';

const addMyselfToTheList = (users: readonly User[], myUser: Maybe<User>): readonly User[] => {
    if (myUser.isNothing) {
        return users;
    }

    const userExists = users.some((user) => user.id === myUser.value.id);

    return userExists ? users : [...users, myUser.value];
};

export const useOneUserOptionsQuery = (userId: string): UseQueryResult<SingleUserOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/options`;

    return useQuery<SingleUserOptions>({
        enabled: is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(singleUserOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

const fetchUsersPageSize = 100;
const usersQueryStaleTime = 300_000; // 5 minutes
const sortUsersAlphabetically = (A: User, B: User): number =>
    getUserName(Maybe.of(A)).localeCompare(getUserName(Maybe.of(B)));

type UsersQueryResult = {
    readonly userQuery: UseInfiniteQueryResult;
    readonly users: readonly User[];
};

type UsersQueryOptions = {
    readonly enabled: boolean;
};

export const useUsersQuery = (options: UsersQueryOptions): UsersQueryResult => {
    const { enabled } = options;
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = '/users';

    const userQuery = useInfiniteQuery<
        PaginatedResponse<User>,
        DefaultError,
        InfiniteData<PaginatedResponse<User>>,
        QueryKey,
        number
    >({
        enabled,
        getNextPageParam: (lastPage) => {
            const { page, pages } = lastPage.pagination;
            const nextPage = page + 1;

            return nextPage < pages ? nextPage : null;
        },
        initialPageParam: 0,
        placeholderData: keepPreviousData,
        queryFn: async ({ pageParam }) => {
            const result = await validatingCobrainerApiFetcher.get(userPaginationSchema, {
                pathname,
                requestOptions: {
                    searchParams: {
                        page: pageParam,
                        size: fetchUsersPageSize,
                    },
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
        staleTime: usersQueryStaleTime,
    });

    useEffect(() => {
        // This forces all pages to be fetched.
        if (userQuery.hasNextPage === true && !userQuery.isFetchingNextPage) {
            userQuery.fetchNextPage();
        }
    });

    const users = useMemo(() => {
        const usersData = userQuery.data?.pages.flatMap((page) => page.data) ?? [];

        return [...usersData].sort(sortUsersAlphabetically);
    }, [userQuery.data]);

    return {
        userQuery,
        users,
    };
};

export const useRecruitersQuery = (): {
    readonly recruiters: readonly User[];
    readonly recruitersQuery: UseInfiniteQueryResult;
} => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const myUserProfile = applicationState.myUserProfile;

    const pathname: Pathname = '/users';

    const recruitersQuery = useInfiniteQuery<
        PaginatedResponse<User>,
        DefaultError,
        InfiniteData<PaginatedResponse<User>>,
        QueryKey,
        number
    >({
        getNextPageParam: (lastPage) => {
            const { page, pages } = lastPage.pagination;
            const nextPage = page + 1;

            return nextPage < pages ? nextPage : null;
        },
        initialPageParam: 0,
        placeholderData: keepPreviousData,
        queryFn: async ({ pageParam }) => {
            const result = await validatingCobrainerApiFetcher.get(userPaginationSchema, {
                pathname,
                requestOptions: {
                    searchParams: {
                        page: pageParam,
                        role: 'recruiter',
                        size: fetchUsersPageSize,
                    },
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', 'recruiter'],
        staleTime: usersQueryStaleTime,
    });

    useEffect(() => {
        // This forces all pages to be fetched.
        if (recruitersQuery.hasNextPage === true && !recruitersQuery.isFetchingNextPage) {
            recruitersQuery.fetchNextPage();
        }
    });

    const usersData = recruitersQuery.data?.pages.flatMap((page) => page.data) ?? [];
    const recruitersWithMe = addMyselfToTheList(usersData, myUserProfile);
    const recruiters = [...recruitersWithMe].sort(sortUsersAlphabetically);

    return {
        recruiters,
        recruitersQuery,
    };
};

export const useHiringManagersQuery = (): {
    readonly hiringManagers: readonly User[];
    readonly hiringManagersQuery: UseInfiniteQueryResult;
} => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const myUserProfile = applicationState.myUserProfile;
    const pathname: Pathname = '/users';

    const hiringManagersQuery = useInfiniteQuery<
        PaginatedResponse<User>,
        DefaultError,
        InfiniteData<PaginatedResponse<User>>,
        QueryKey,
        number
    >({
        getNextPageParam: (lastPage) => {
            const { page, pages } = lastPage.pagination;
            const nextPage = page + 1;

            return nextPage < pages ? nextPage : null;
        },
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const result = await validatingCobrainerApiFetcher.get(userPaginationSchema, {
                pathname,
                requestOptions: {
                    searchParams: {
                        page: pageParam,
                        role: 'hiring_manager',
                        size: fetchUsersPageSize,
                    },
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'hiring_manager', 'GET'],
        staleTime: usersQueryStaleTime,
    });

    useEffect(() => {
        if (hiringManagersQuery.hasNextPage === true && !hiringManagersQuery.isFetchingNextPage) {
            hiringManagersQuery.fetchNextPage();
        }
    });

    const usersData = hiringManagersQuery.data?.pages.flatMap((page) => page.data) ?? [];
    const hiringManagersWithMe = addMyselfToTheList(usersData, myUserProfile);
    const hiringManagers = [...hiringManagersWithMe].sort(sortUsersAlphabetically);

    return {
        hiringManagers,
        hiringManagersQuery,
    };
};

export const useOneUserQuery = (userId: string): { readonly userQuery: UseQueryResult<User> } => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}`;

    const userQuery = useQuery({
        enabled: is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });

    return { userQuery };
};

export const useUserCustomAttributes = (userId: string): readonly CustomAttributeWithValue[] => {
    const { data: userOptions } = useOneUserOptionsQuery(userId);
    const { userQuery } = useOneUserQuery(userId);

    return mapToAttributesWithValue(Maybe.of(userOptions?.customAttributes), Maybe.of(userQuery.data?.custom));
};

export const useUserSkillsLimits: () => {
    readonly hardSoftLimit: number;
    readonly languageLimit: number;
} = () => {
    const { applicationState } = useContext(ApplicationContext);

    const userConfiguration = applicationState.customization.get('usersConfiguration');

    return {
        hardSoftLimit: userConfiguration.get('userSkillCountLimit').unwrapOr(50),
        languageLimit: userConfiguration.get('userLanguageCountLimit').unwrapOr(10),
    };
};
